import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BrainCircuit, Loader2, Send } from "lucide-react";

const STARTERS = [
  "Is my house edge healthy right now?",
  "Explain exactly how pack odds work",
  "What needs my attention today?",
  "How do bounty payouts get protected?",
  "Walk me through the signed-card flow",
];

export default function AdminBrain() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sidRef = useRef(null);

  if (!sidRef.current) {
    sidRef.current = localStorage.getItem("admin_brain_sid") || `brain-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("admin_brain_sid", sidRef.current);
  }

  useEffect(() => {
    api.get(`/admin/assistant/history?session_id=${sidRef.current}`)
      .then(({ data }) => setMsgs(data)).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/assistant/chat", { message: q, session_id: sidRef.current }, { timeout: 120000 });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The Brain didn't respond — try again");
      setMsgs((m) => m.slice(0, -1));
      setInput(q);
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="admin-brain">
      <div className="flex items-center gap-2 mb-1">
        <BrainCircuit className="w-4 h-4 text-pb-cyan" />
        <span className="font-display font-bold uppercase">Bullpen Brain — Owner's AI</span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Your private operations AI. It knows every formula, law and lever in the ecosystem — and it reads the LIVE numbers
        (house ledger, odds, treasury, pending approvals) before every answer. Ask it anything about how the system works
        or how to keep it running right.
      </p>

      <div className="border border-pb-border bg-pb-elevated/30 h-80 overflow-y-auto p-3 space-y-3 mb-3" data-testid="brain-messages">
        {msgs.length === 0 && !busy && (
          <div className="text-xs text-zinc-600 pt-2">Ask your first question — or tap a starter below.</div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "bg-pb-cyan/15 border border-pb-cyan/40 text-white" : "bg-pb-elevated border border-pb-border text-zinc-200"}`}
              data-testid={`brain-msg-${m.role}-${i}`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="brain-thinking">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-pb-cyan" /> Reading the live system…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {STARTERS.map((s) => (
          <button key={s} data-testid={`brain-starter-${s.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}
            onClick={() => send(s)} disabled={busy}
            className="border border-pb-border text-zinc-400 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-40">
            {s}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input data-testid="brain-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about odds, house edge, payouts, pricing, any feature…"
          className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan" />
        <button data-testid="brain-send" onClick={() => send()} disabled={busy || !input.trim()}
          className="flex items-center gap-1.5 bg-pb-cyan text-black font-black uppercase tracking-widest px-5 py-2.5 text-xs hover:opacity-90 active:scale-95 transition-all disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Ask
        </button>
      </div>
    </div>
  );
}
